import { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AppShell } from '../components/layout/AppShell';
import { ProtectedRoute, RequireRole } from '../routes/guards';
import { AppRole } from '../lib/auth/roles';
import { AUTH_ENABLED } from '../lib/auth/provider';

const LandingPage = lazy(async () => ({ default: (await import('../pages/LandingPage')).LandingPage }));
const WizardPage = lazy(async () => ({ default: (await import('../pages/WizardPage')).WizardPage }));
const ProjectsPage = lazy(async () => ({ default: (await import('../pages/ProjectsPage')).ProjectsPage }));
const NewProjectPage = lazy(async () => ({ default: (await import('../pages/NewProjectPage')).NewProjectPage }));
const DatasetsPage = lazy(async () => ({ default: (await import('../pages/DatasetsPage')).DatasetsPage }));
const DatasetDetailPage = lazy(async () => ({ default: (await import('../pages/DatasetDetailPage')).DatasetDetailPage }));
const ModelsPage = lazy(async () => ({ default: (await import('../pages/ModelsPage')).ModelsPage }));
const NotificationsPage = lazy(async () => ({ default: (await import('../pages/NotificationsPage')).NotificationsPage }));
const AdminPage = lazy(async () => ({ default: (await import('../pages/AdminPage')).AdminPage }));
const ObservabilityPage = lazy(async () => ({ default: (await import('../pages/ObservabilityPage')).ObservabilityPage }));
const LoginPage = lazy(async () => ({ default: (await import('../pages/LoginPage')).LoginPage }));

function PageSpinner() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="h-7 w-7 rounded-full border-2 border-mist border-t-accent animate-spin" />
    </div>
  );
}

function withSuspense(element: React.ReactNode) {
  return <Suspense fallback={<PageSpinner />}>{element}</Suspense>;
}

export const router = createBrowserRouter([
  {
    // When auth is disabled (demo mode) redirect /login → /.
    // When auth is enabled, show the real login page.
    path: '/login',
    element: AUTH_ENABLED ? withSuspense(<LoginPage />) : <Navigate to="/" replace />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: withSuspense(<LandingPage />) },
      { path: 'wizard', element: withSuspense(<WizardPage />) },
      { path: 'projects', element: withSuspense(<ProjectsPage />) },
      { path: 'projects/new', element: withSuspense(<NewProjectPage />) },
      { path: 'datasets', element: withSuspense(<DatasetsPage />) },
      { path: 'datasets/:id', element: withSuspense(<DatasetDetailPage />) },
      {
        path: 'models',
        element: withSuspense(
          <RequireRole role={AppRole.SUPERADMIN}>
            <ModelsPage />
          </RequireRole>,
        ),
      },
      { path: 'notifications', element: withSuspense(<NotificationsPage />) },
      {
        path: 'admin',
        element: withSuspense(
          <RequireRole role={AppRole.ADMIN}>
            <AdminPage />
          </RequireRole>,
        ),
      },
      {
        path: 'observability',
        element: withSuspense(
          <RequireRole role={AppRole.SUPERADMIN}>
            <ObservabilityPage />
          </RequireRole>,
        ),
      },
    ],
  },
]);
